import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { environment } from '@env/environment';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { Microservice } from '@hypenation/shared';
import { Customer } from '../models/customer.model';

@Injectable({
  providedIn: 'root',
})
export class CustomerProfileService {
  private readonly apiUrl: string = environment.apiUrl;
  private readonly _profile = signal<Customer | null>(null);
  private loadingForEmail: string | null = null;

  /**
   * Reactive read of the currently loaded customer profile.
   */
  public readonly profile = this._profile.asReadonly();

  constructor(private readonly _http: HttpClient) {}

  /**
   * Fetch the customer profile by email and cache it. Calling repeatedly
   * for the same email is a no-op once a profile is loaded.
   */
  public loadProfile(email: string | null | undefined): Observable<Customer | null> {
    if (!email) {
      return of(null);
    }

    const existing = this._profile();
    if (existing && existing.email === email) {
      return of(existing);
    }

    if (this.loadingForEmail === email) {
      return of(existing);
    }

    this.loadingForEmail = email;

    const httpOptions = {
      headers: new HttpHeaders({ Accept: 'application/json' }),
      observe: 'response' as const,
    };

    return this._http
      .get<Customer>(
        `${this.apiUrl}/${Microservice.CRM}/admin/v1/customers/email?email=${encodeURIComponent(email)}`,
        httpOptions
      )
      .pipe(
        map((res: HttpResponse<Customer>) => res.body ?? null),
        tap((customer) => {
          if (customer) {
            this._profile.set(customer);
          }
          this.loadingForEmail = null;
        }),
        catchError((err) => {
          console.error('Failed to load customer profile:', err);
          this.loadingForEmail = null;
          return of(null);
        })
      );
  }

  public clear(): void {
    this._profile.set(null);
    this.loadingForEmail = null;
  }
}
